import { Tooltip } from '@material-ui/core';
import MaterialTable, { Column } from 'material-table';
import React from 'react';

import { ISite, IUser } from '~/src/dash/api/console-api';
import { convertECurrencyTypeToSymbol, ECurrencyType } from '~/src/dash/api/exchange-rates';
import { IStore } from '~/src/dash/reducers/store';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { ct } from '~/src/dash/publicaUI';

export interface IProps {
  channels: ISite[];
  onSelectRow?: (id: string) => void;
  title?: string;
  interactive?: boolean;
  bidderFloor: number;
  bidderFloorUSD: number;
  isHonorChannelFloorEnabled: boolean;
  user: IUser;
  currencyDisplayed: ECurrencyType;
}

interface IState {
  columns: Array<Column<ISite>>;
  data: ISite[];
}

interface ISiteExtended extends ISite {
  BidFloorUSD: number;
}

function renderBidFloorRow(
  channel: ISiteExtended,
  bidderFloor: number,
  bidderFloorUSD: number,
  isHonorChannelFloorEnabled: boolean,
  currencyDisplayed: ECurrencyType,
): any {
  if (isHonorChannelFloorEnabled && bidderFloorUSD <= channel.BidFloorUSD) {
    return (
      <Tooltip title='Channel Floor takes precedence'>
        <p style={{ color: publicaColors.darkGreen }}>
          {convertECurrencyTypeToSymbol(channel.CurrencyDisplayed)}
          {channel.BidFloor}
        </p>
      </Tooltip>
    );
  }
  return (
    <p style={{ color: publicaColors.black }}>
      {convertECurrencyTypeToSymbol(currencyDisplayed)} {bidderFloor}
    </p>
  );
}

function formatSiteName(user: IUser, site: ISite): string {
  return checkUserInfoMasked(user) ? 'demo_channel_' + site.ID : site.Name;
}

export const BidFloorTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      columns: [
        { title: ct('ID'), field: 'ID', searchable: false },
        {
          title: ct('Name'),
          field: 'Name',
          render: (rowData: ISite) =>
            this.props.interactive ? (
              <Tooltip title='Open this channel'>
                <p>{formatSiteName(this.props.user, rowData)}</p>
              </Tooltip>
            ) : (
              <p>{formatSiteName(this.props.user, rowData)}</p>
            ),
        },
        {
          title: ct('BidFloor'),
          field: 'BidFloor',
          render: (rowData: ISiteExtended) =>
            renderBidFloorRow(
              rowData,
              this.props.bidderFloor,
              this.props.bidderFloorUSD,
              this.props.isHonorChannelFloorEnabled,
              this.props.currencyDisplayed,
            ),
        },
      ],
      data: this.props.channels,
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.channels !== this.props.channels) {
        this.setState({
          data: this.props.channels,
        });
      }
    }

    handleRowClick = (_: undefined, rowData: ISite) => {
      if (this.props.onSelectRow) {
        this.props.onSelectRow(rowData.ID.toString());
      }
    };

    render() {
      let fieldProps = {
        style: {
          paddingLeft: '20px',
          paddingRight: '20px',
          width: '100%',
        },
        title: this.props.title || 'Targeted Channels Bid Floor',
        options: {
          pageSize: 15,
          pageSizeOptions: [5, 15, 30],
        },
        columns: this.state.columns,
        data: this.state.data,
        localization: {
          toolbar: {
            searchPlaceholder: ct('Search'),
          },
        },
      };

      if (this.props.interactive) {
        fieldProps = Object.assign(fieldProps, { onRowClick: this.handleRowClick });
      }

      return <MaterialTable {...fieldProps} />;
    }
  },
);
